import { message } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { GlobalApi } from "./GlobalApi";
import type { FollowSpaceCard, RecentSpaceEntry, UserInfo, ComplianceLevelCode } from "./GlobalType";
import { isNicknameMissing } from "./userProfileUtils";

class GlobalModel {
  currentUser: UserInfo | null = null;
  isLoggedIn = false;
  initialized = false;
  /** token 校验结束后为 true，避免直链进入时误判未登录 */
  authReady = false;
  recentSpaceEntries: RecentSpaceEntry[] = [];
  followSpaceCards: FollowSpaceCard[] = [];
  complianceLevelCode: ComplianceLevelCode = "finance_share";

  /**
   * 已登录但昵称为空时强制展示资料补全层（与 Web 一致，无取消入口）。
   */
  profileSetupRequired = false;

  /**
   * 用户主动打开同套编辑弹层（有取消/点遮罩关闭，与首登强制区分）。
   */
  profileEditorOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  get financeShareEnabled(): boolean {
    return this.complianceLevelCode === "finance_share";
  }

  async loadComplianceLevel() {
    const level = await GlobalApi.getComplianceLevel();
    runInAction(() => {
      this.complianceLevelCode = level;
    });
  }

  async init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    void this.loadComplianceLevel();

    try {
      const hadToken = !!localStorage.getItem("token");
      if (!hadToken) {
        return;
      }
      const user = await GlobalApi.getUserByToken();
      if (user) {
        runInAction(() => {
          this.isLoggedIn = true;
          this.currentUser = user;
          this.syncProfileSetupGate();
        });
        void this.loadRecentSpaces();
        void this.loadFollowSpaces();
      } else {
        runInAction(() => {
          localStorage.removeItem("token");
          this.isLoggedIn = false;
          this.currentUser = null;
          this.recentSpaceEntries = [];
          this.followSpaceCards = [];
          this.profileSetupRequired = false;
          this.profileEditorOpen = false;
        });
        message.warning("登录已过期，请重新登录");
      }
    } finally {
      runInAction(() => {
        this.authReady = true;
      });
    }
  }

  setLoggedIn(logged: boolean, user: UserInfo | null) {
    this.isLoggedIn = logged;
    this.currentUser = user;
    if (!logged) {
      localStorage.removeItem("token");
      this.recentSpaceEntries = [];
      this.followSpaceCards = [];
      this.profileSetupRequired = false;
      this.profileEditorOpen = false;
    } else {
      this.syncProfileSetupGate();
      void this.loadRecentSpaces();
      void this.loadFollowSpaces();
    }
  }

  syncProfileSetupGate() {
    this.profileSetupRequired = this.isLoggedIn && isNicknameMissing(this.currentUser);
  }

  openProfileEditor() {
    this.profileEditorOpen = true;
  }

  closeProfileEditor() {
    this.profileEditorOpen = false;
  }

  async logout() {
    await GlobalApi.logoutApi();
    runInAction(() => {
      this.setLoggedIn(false, null);
    });
  }

  async loadRecentSpaces() {
    if (!this.isLoggedIn) {
      runInAction(() => {
        this.recentSpaceEntries = [];
      });
      return;
    }
    const rows = await GlobalApi.getRecentSpaceVisits();
    runInAction(() => {
      this.recentSpaceEntries = rows;
    });
  }

  async loadFollowSpaces() {
    if (!this.isLoggedIn) {
      runInAction(() => {
        this.followSpaceCards = [];
      });
      return;
    }
    const rows = await GlobalApi.listFollowSpaceCards();
    runInAction(() => {
      this.followSpaceCards = rows;
    });
  }

  async refreshUserFromServer() {
    const user = await GlobalApi.getUserByToken();
    if (user) {
      runInAction(() => {
        this.isLoggedIn = true;
        this.currentUser = user;
        this.syncProfileSetupGate();
      });
    } else {
      runInAction(() => {
        localStorage.removeItem("token");
        this.isLoggedIn = false;
        this.currentUser = null;
        this.recentSpaceEntries = [];
        this.followSpaceCards = [];
        this.profileSetupRequired = false;
        this.profileEditorOpen = false;
      });
    }
  }
}

export const globalModel = new GlobalModel();
